import { useState, useEffect } from 'react'

/**
 * Module-level sync tracker.
 * Each active onSnapshot listener reports its path's hasPendingWrites here.
 * AppLayout (and any other consumer) reads the aggregate via useSyncStatus().
 */
const pendingPaths = new Set<string>()
type Listener = (hasPending: boolean) => void
const listeners = new Set<Listener>()

function notify() {
  const hasPending = pendingPaths.size > 0
  listeners.forEach((l) => l(hasPending))
}

/** Called by useFirestore on every snapshot */
export function updateSyncPath(path: string, hasPendingWrites: boolean) {
  if (hasPendingWrites) {
    pendingPaths.add(path)
  } else {
    pendingPaths.delete(path)
  }
  notify()
}

/** Called by useFirestore on cleanup so stale paths don't linger */
export function clearSyncPath(path: string) {
  pendingPaths.delete(path)
  notify()
}

export function useSyncStatus() {
  const [hasPendingWrites, setHasPendingWrites] = useState(pendingPaths.size > 0)

  useEffect(() => {
    const listener: Listener = (v) => setHasPendingWrites(v)
    listeners.add(listener)
    // sync with current state in case it changed between render and effect
    setHasPendingWrites(pendingPaths.size > 0)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  return { hasPendingWrites }
}
